import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import {
  type EnterpriseWorkspaceInterventionRow,
} from '../lib/enterprise-api';
import { loadWorkspaceInterventions } from '../lib/enterprise-work-engine';

export function useEnterpriseWorkspaceInterventions(days = 30) {
  const { accessToken } = useAuth();
  const { activeOrgId, hasActiveOrgAccess } = useEnterpriseOrg();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<'organization' | 'self' | null>(null);
  const [roleAction, setRoleAction] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState<number | null>(null);
  const [responseSlaHours, setResponseSlaHours] = useState<number | null>(null);
  const [slaBreaches, setSlaBreaches] = useState<number | null>(null);
  const [queue, setQueue] = useState<EnterpriseWorkspaceInterventionRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !activeOrgId || !hasActiveOrgAccess) {
      setQueue([]);
      setScope(null);
      setRoleAction(null);
      setWindowDays(null);
      setResponseSlaHours(null);
      setSlaBreaches(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    void loadWorkspaceInterventions(accessToken, activeOrgId, days)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setQueue([]);
          setScope(null);
          setRoleAction(null);
          setWindowDays(null);
          setResponseSlaHours(null);
          setSlaBreaches(null);
          setError(res.error || 'Failed to load intervention queue');
          return;
        }
        setQueue(res.queue || []);
        setScope(res.scope || null);
        setRoleAction(res.roleAction || null);
        setWindowDays(res.windowDays ?? null);
        setResponseSlaHours(res.responseSlaHours ?? null);
        setSlaBreaches(res.slaBreaches ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setQueue([]);
        setScope(null);
        setRoleAction(null);
        setWindowDays(null);
        setResponseSlaHours(null);
        setSlaBreaches(null);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, activeOrgId, hasActiveOrgAccess, days]);

  return { loading, error, scope, roleAction, windowDays, responseSlaHours, slaBreaches, queue };
}

